import classNames from 'classnames'
import { AddressPopup } from 'feature/addresses/components/AddressPopup/AddressPopup'
import { selectAddress } from 'feature/addresses/redux/addressCreateSelector'
import { FormInputText } from 'feature/formComponents'
import { ScreenEnum, useScreen } from 'feature/screenMode'
import { ControlsType, MarkType } from 'feature/YandexMap/types'
import { YandexMap } from 'feature/YandexMap/YandexMap'
import React, { FormEvent, memo, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useLocation } from 'react-use'
import { useActions, useAppSelector } from 'store/hooks'
import { Button } from 'ui'
import { ButtonProps } from 'ui/Button/Button'
import { AddressSelectInput } from 'widget/AddressSelectInput'
import { LoaderInCentalPosition } from 'widget/LoaderInCentalPosition/LoaderInCentalPosition'
import { CheckboxesBlock } from 'widget/UserDataForm/CheckboxesBlock/CheckboxesBlock'

import style from './UserDataForm.module.scss'

export type UserDataFormProps = {
  title: string
  sendButton: Pick<ButtonProps, 'endIcon' | 'startIcon'> & {
    text: string
    disabled?: boolean
    isLoading?: boolean
  }
  onSubmit: () => void
  fieldsWrapperClass?: string
  titleIcon?: React.ReactNode
  headerButton?: Pick<ButtonProps, 'endIcon' | 'startIcon'> & {
    text: string
    onClick: () => void
  }
  isLkm?: boolean
  isFirstStep?: boolean
  hasAdvCheckbox?: boolean
}
// форма регистрации
export const UserDataForm = memo(
  ({
    title,
    titleIcon,
    onSubmit,
    sendButton,
    fieldsWrapperClass,
    headerButton,
    isLkm,
    isFirstStep = false,
    hasAdvCheckbox = true,
  }: UserDataFormProps) => {
    const [viewMode, setViewMode] = useState<string>('form')
    const screen = useScreen()
    const { pathname } = useLocation()

    const { isValid: isValidAddress, isDirty: isDirtyAddress } =
      useAppSelector(selectAddress)
    const { setIsDirtyAddress } = useActions()

    const {
      formState: { isValid: isValidForm, isDirty: isDirtyForm },
    } = useFormContext()

    const { authInfo } = useAppSelector(state => state.auth)

    const openCityPopupHandler = () => {
      if (screen !== ScreenEnum.desktop) {
        setViewMode('popup')
      }
    }

    useEffect(() => {
      setIsDirtyAddress(false)
      return () => {
        setIsDirtyAddress(false)
      }
    }, [])

    const handleSubmit = (e: FormEvent) => {
      e.preventDefault()
      onSubmit()
    }

    const isDirty = isDirtyAddress || isDirtyForm
    const isValid = isValidAddress && isValidForm

    const buttonDisabled = isFirstStep
      ? !isValid || authInfo.isLoading
      : !isDirty || !isValid || authInfo.isLoading

    switch (viewMode) {
      case 'popup':
        return <AddressPopup setViewMode={setViewMode} />
      default: // 'form'
        return (
          <div className={style.formWrapper}>
            <form className={style.regForm} noValidate onSubmit={handleSubmit}>
              <div className={style.inputsWrapper}>
                <div
                  className={classNames(style.textFields, {
                    fieldsWrapperClass,
                  })}
                >
                  <div className={style.formHeader}>
                    <div className={style.title}>
                      {titleIcon && titleIcon}
                      <span className='h1'>{title}</span>
                    </div>
                    {headerButton && (
                      <Button
                        className={style.headerButton}
                        endIcon={headerButton.endIcon}
                        startIcon={headerButton.startIcon}
                        onClick={headerButton.onClick}
                        data-testid='user-data-back'
                      >
                        {headerButton.text}
                      </Button>
                    )}
                  </div>
                  <FormInputText
                    name='lastname'
                    inputProps={{
                      label: 'Фамилия',
                      required: true,
                      inputProps: { maxLength: 100 },
                      'data-testid': 'user-data-last-name',
                    }}
                  />
                  <FormInputText
                    name='firstname'
                    inputProps={{
                      label: 'Имя',
                      required: true,
                      inputProps: { maxLength: 100 },
                      'data-testid': 'user-data-first-name',
                    }}
                  />
                  <FormInputText
                    name='middlename'
                    inputProps={{
                      label: 'Отчество',
                      inputProps: { maxLength: 100 },
                      helperText: isFirstStep
                        ? 'Необходимо указать данные в соответствии с сертификатом ДорХан'
                        : '',
                      'data-testid': 'user-data-middle-name',
                    }}
                  />
                  {isLkm && (
                    <FormInputText
                      name='email'
                      inputProps={{
                        label: 'E-mail',
                        required: true,
                        type: 'email',
                        'data-testid': 'user-data-email',
                      }}
                    />
                  )}
                </div>

                <AddressSelectInput
                  onClick={openCityPopupHandler}
                  className={style.AddressSelectInput}
                  helperText={
                    isFirstStep
                      ? 'Укажите местоположение, с которого вы будете добираться до объектов'
                      : ''
                  }
                  inputProps={{
                    inputProps: { 'data-testid': 'user-data-address' },
                  }}
                />
                <YandexMap
                  className={style.map}
                  marks={[MarkType.geolocationMark, MarkType.clickMark]}
                  controls={[
                    ControlsType.GeolocationControlUnit,
                    ControlsType.ZoomControl,
                  ]}
                />
              </div>
              {pathname === '/registration' && (
                <div className={style.checkboxes}>
                  <CheckboxesBlock hasAdvCheckbox={hasAdvCheckbox} />
                </div>
              )}

              <Button
                type='submit'
                endIcon={sendButton.endIcon}
                startIcon={sendButton.startIcon}
                disabled={buttonDisabled}
                size='medium'
                variant='contained'
                data-testid='user-data-submit'
              >
                {authInfo.isLoading && (
                  <LoaderInCentalPosition className={style.loader} />
                )}
                {sendButton.text}
              </Button>
            </form>
          </div>
        )
    }
  },
)
